<script lang="ts">
	import type { MetricSeries } from '../../AppTypes';

	export let series: MetricSeries;

	const width = 720;
	const height = 180;
	const padding = 28;

	// String-valued metrics can't be plotted numerically — show them as a list of
	// value changes (consecutive identical samples collapsed into one entry).
	$: isNumeric = series.points.every((p) => typeof p.value === 'number');
	$: changes = series.points.reduce<{ t: string; value: number | string }[]>((acc, p) => {
		if (acc.length === 0 || acc[acc.length - 1].value !== p.value) {
			acc.push({ t: p.t, value: p.value });
		}
		return acc;
	}, []);

	function fmtChange(t: string): string {
		return new Date(t).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
	}

	$: pts = series.points.map((p) => ({ x: new Date(p.t).getTime(), y: p.value as number }));
	$: minX = pts.length > 0 ? Math.min(...pts.map((p) => p.x)) : 0;
	$: maxX = pts.length > 0 ? Math.max(...pts.map((p) => p.x)) : 1;
	$: minY = pts.length > 0 ? Math.min(...pts.map((p) => p.y)) : 0;
	$: maxY = pts.length > 0 ? Math.max(...pts.map((p) => p.y)) : 1;
	$: spanX = maxX - minX || 1;
	$: spanY = maxY - minY || 1;

	function sx(x: number): number {
		return padding + ((x - minX) / spanX) * (width - 2 * padding);
	}
	function sy(y: number): number {
		return height - padding - ((y - minY) / spanY) * (height - 2 * padding);
	}

	$: line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(p.x).toFixed(1)} ${sy(p.y).toFixed(1)}`).join(' ');
	$: area =
		pts.length > 0
			? `${line} L ${sx(pts[pts.length - 1].x).toFixed(1)} ${height - padding} L ${sx(pts[0].x).toFixed(1)} ${height - padding} Z`
			: '';

	function fmtTime(ms: number): string {
		return new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
	}
	$: last = pts.length > 0 ? pts[pts.length - 1].y : null;
</script>

<div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
	<div class="flex items-center justify-between mb-2">
		<div>
			<span class="font-medium text-gray-900 dark:text-white">{series.label}</span>
			<span class="text-xs text-gray-400 ml-2">{series.key}{series.unit ? ` (${series.unit})` : ''}</span>
		</div>
		{#if last !== null}
			<span class="text-sm text-gray-500 dark:text-gray-400">last: {last}{series.unit ?? ''}</span>
		{/if}
	</div>

	{#if series.points.length === 0}
		<p class="text-sm text-gray-400 py-6 text-center">No data for this metric in the selected range.</p>
	{:else if !isNumeric}
		<ul class="text-sm divide-y divide-gray-100 dark:divide-gray-700">
			{#each changes as c}
				<li class="flex items-center justify-between py-1.5">
					<span class="font-mono text-gray-900 dark:text-white">{c.value}</span>
					<span class="text-xs text-gray-400">{fmtChange(c.t)}</span>
				</li>
			{/each}
		</ul>
		<p class="text-xs text-gray-400 mt-2">{series.points.length} samples, {changes.length} change{changes.length === 1 ? '' : 's'}</p>
	{:else if pts.length > 1}
		<svg viewBox={`0 0 ${width} ${height}`} class="w-full" style="height: 180px" preserveAspectRatio="none">
			<path d={area} fill="#3b82f6" opacity="0.10" />
			<path d={line} fill="none" stroke="#3b82f6" stroke-width="1.5" />
			<text x={padding} y="12" class="fill-gray-400" font-size="10">{maxY}</text>
			<text x={padding} y={height - padding + 14} class="fill-gray-400" font-size="10">{minY}</text>
		</svg>
		<div class="flex justify-between text-xs text-gray-400 mt-1">
			<span>{fmtTime(minX)}</span>
			<span>{pts.length} points</span>
			<span>{fmtTime(maxX)}</span>
		</div>
	{:else}
		<p class="text-sm text-gray-500 py-6 text-center">Only one data point ({pts[0].y}{series.unit ?? ''}) in this range.</p>
	{/if}
</div>
